import {useCallback, useMemo, useState} from 'react'
import styles from './ToolProposeWork.module.css'
import {Button, Spinner} from '@primer/react'
import {CheckCircleIcon, MaximizeIcon, PlayIcon} from '@primer/octicons-react'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import type {CreateAPITaskParams} from '../../../../types/api-task'
import {useCreateAPITask} from '../../../../hooks/use-create-api-task'
import {getAgentTasksQueryKeyForMutation, PAGE_SIZE} from '../../../../hooks/useTasks'
import {useSessionLogsContext} from '../../Context'
import {NewRepoAgentTaskButton} from '@github-ui/new-repo-agent-task-button/NewRepoAgentTaskButton'
import type {Repository} from '@github-ui/current-repository'
import {PROPOSE_WORK_MAX_TEXTAREA_HEIGHT} from '../../../../utils/constants'
import {useCurrentUser} from '../../../../contexts/CurrentUserContext'
import {useReadOnlyAhpHistory} from '../../ahp/AhpSessionContext'

export interface ToolProposeWorkProps {
  title: string
  description: string
  type: 'code_change' | 'task'
}

export function ToolProposeWork({title, description, type}: ToolProposeWorkProps) {
  const readOnlyHistory = useReadOnlyAhpHistory()
  if (readOnlyHistory) {
    return (
      <div className={styles.container}>
        <p>
          <strong>{title}</strong>
        </p>
        <MarkdownRenderer markdown={description} />
      </div>
    )
  }
  return <InteractiveToolProposeWork title={title} description={description} type={type} />
}

function InteractiveToolProposeWork({title, description, type}: ToolProposeWorkProps) {
  const {createTask, isCreating} = useCreateAPITask({
    queryKey: getAgentTasksQueryKeyForMutation(),
    pageSize: PAGE_SIZE,
  })

  const {session, sessionNwo} = useSessionLogsContext()
  const {currentUser} = useCurrentUser()
  const [isStarted, setIsStarted] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      e.stopPropagation()

      // If the task is already started or being created, do nothing
      if (isStarted || isCreating) {
        return
      }

      // We can only create a task if we have the nwo for the current session;
      // that's determined by the session's pull request.
      if (!sessionNwo) {
        return
      }

      const newTask: CreateAPITaskParams = {
        problemStatement: description,
        eventType: 'propose_work',
        eventContent: `Proposed work: ${title}`,
        nwo: sessionNwo,
        createPullRequest: type === 'code_change',
        eventIdentifiers: session?.workflow_run_id ? [session.workflow_run_id.toString()] : undefined,
      }

      createTask(newTask)
      setIsStarted(true)
    },
    [title, description, type, createTask, sessionNwo, isStarted, isCreating, session],
  )

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't open dialog if it's already open or if clicking on interactive elements
      if (isDialogOpen) {
        return
      }
      const target = e.target as HTMLElement
      // Don't open dialog when clicking on interactive elements (buttons, forms, links, inputs)
      if (target.closest('button') || target.closest('form') || target.closest('a') || target.closest('input')) {
        return
      }
      setIsDialogOpen(true)
    },
    [isDialogOpen],
  )

  const [ownerLogin, repoName] = sessionNwo ? sessionNwo.split('/') : [null, null]

  const initialRepo =
    sessionNwo && repoName && ownerLogin && session?.repo_id
      ? ({
          nameWithOwner: sessionNwo,
          name: repoName,
          ownerLogin,
          id: session.repo_id,
        } as Repository)
      : undefined

  return (
    <>
      {/* Mouse-only click-anywhere shortcut from #12307; presentational because it adds no semantics of its own and keyboard/AT reach the same dialog via the "Edit task" IconButton. */}
      <div className={styles.container} role="presentation" onClick={handleCardClick}>
        <div className={styles.titleContainer}>
          <h3 className="h4">{title}</h3>
          <div className={styles.buttonsContainer}>
            {initialRepo && (
              <NewRepoAgentTaskButton
                icon={MaximizeIcon}
                label="Edit task"
                repository={initialRepo}
                currentUserId={currentUser?.id}
                currentUserLogin={currentUser?.login}
                eventType="propose_work"
                size="small"
                variant="invisible"
                initialText={description}
                maxTextareaHeight={PROPOSE_WORK_MAX_TEXTAREA_HEIGHT}
                isOpen={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                eventIdentifiers={session?.workflow_run_id ? [session.workflow_run_id.toString()] : undefined}
                taskModeEnabled={false}
              />
            )}
            <form onSubmit={handleSubmit} className={styles.startTaskButton}>
              <StartTaskButton isCreating={isCreating} isStarted={isStarted} />
            </form>
          </div>
        </div>

        <div className={styles.descriptionContainer}>
          <MarkdownRenderer markdown={description} />
        </div>
      </div>
    </>
  )
}

function StartTaskButton({isCreating, isStarted}: {isCreating: boolean; isStarted: boolean}) {
  const [text, icon] = useMemo(() => {
    if (isCreating) {
      return ['Creating...', ButtonSpinner]
    }
    if (isStarted) {
      return ['Task started', CheckCircleIcon]
    }
    return ['Start task', PlayIcon]
  }, [isCreating, isStarted])
  return (
    <Button type="submit" leadingVisual={icon} inactive={isCreating || isStarted}>
      {text}
    </Button>
  )
}

function ButtonSpinner() {
  return <Spinner size="small" srText="Starting task…" />
}
